import { useState } from "react";
import { AlertTriangle, Download, ListOrdered } from "lucide-react";
import { DataTable } from "@/components/DataTable";
import { Notify } from "@/components/ui/notify";
import { Button } from "@/components/ui/button";
import { downloadCsv } from "@/features/dashboard/lib/dashboard-export";
import { useProductionOrders } from "./hooks";
import { ProductionOrderFiltersBar } from "./ProductionOrderFiltersBar";
import { orderColumns } from "./production-orders-columns";
import { toCsv } from "./production-orders-csv";
import { SequenceDrawer } from "./sequence";
import { useAuthStore } from "@/stores/auth.store";
import type {
  ProductionOrder,
  ProductionOrderFilters,
} from "../../types/production-orders.types.js";

// Lista filtrável + exportação CSV. Linha/ação abrem a OP (roteado por status pelo
// index: SCHEDULED→edição, demais→indicadores). Colunas e CSV vivem em arquivos irmãos (≤200 LOC).
export function ProductionOrdersTable({ onOpen }: { onOpen: (o: ProductionOrder) => void }) {
  const [filters, setFilters] = useState<ProductionOrderFilters>({ limit: 50 });
  const [sequenceOpen, setSequenceOpen] = useState(false);
  const isGestor = useAuthStore((s) => s.user?.role) === "GESTOR";
  const query = useProductionOrders(filters);
  const { data, isLoading, isError, error, refetch } = query;
  if (isError) return <Notify.QueryError error={error} onRetry={() => void refetch()} />;
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  // S-5: o CSV exporta só as OPs carregadas (≤ limite da página); quando há mais que isso, avisa que
  // a exportação é parcial (sem mudança de backend) — o usuário deve estreitar o filtro p/ o todo.
  const partialCsv = total > items.length;
  // "Simular fila" precisa de uma LINHA escolhida (a fila é da linha) e de ao
  // menos 2 ordens agendadas nela: comparar sequências de uma ordem só não significa nada.
  const scheduledInLine = filters.lineId ? items.filter((o) => o.status === "SCHEDULED").length : 0;
  const canSimulateQueue = isGestor && !!filters.lineId && scheduledInLine >= 2;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <ProductionOrderFiltersBar filters={filters} onChange={setFilters} count={total} />
        <div className="flex flex-col items-end gap-1">
          {isGestor ? (
            <Button
              variant="outline"
              size="sm"
              disabled={!canSimulateQueue}
              title={
                canSimulateQueue
                  ? undefined
                  : "Filtre por uma linha com pelo menos 2 ordens agendadas para simular a fila."
              }
              onClick={() => setSequenceOpen(true)}
            >
              <ListOrdered className="mr-1 h-4 w-4" /> Simular fila
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            disabled={items.length === 0}
            onClick={() => downloadCsv("ordens-de-producao.csv", toCsv(items))}
          >
            <Download className="mr-1 h-4 w-4" /> Exportar CSV
          </Button>
          {partialCsv ? (
            <span className="flex items-center gap-1 text-[11px] text-rd-warning-700">
              <AlertTriangle className="h-3 w-3 shrink-0" /> Exporta {items.length} de {total} —
              refine o filtro para o conjunto completo.
            </span>
          ) : null}
        </div>
      </div>
      <DataTable
        columns={orderColumns(onOpen)}
        data={items}
        rowKey={(o) => o.id}
        onRowClick={onOpen}
        emptyState={isLoading ? "Carregando…" : "Nenhuma ordem encontrada."}
      />
      {sequenceOpen && filters.lineId ? (
        <SequenceDrawer lineId={filters.lineId} onClose={() => setSequenceOpen(false)} />
      ) : null}
    </div>
  );
}
